/**
 * ForUnity Product Configurator - Core Data Model, UI Render, & Cart Integration
 */

(function() {
  // 1. Default fallback configuration
  const DEFAULT_CONFIG = {
    productId: null,
    productName: "",
    basePrice: 0,
    minimumQuantity: 15,
    quoteThreshold: 500,
    discountTiers: {
      "15": 0,
      "30": 5,
      "100": 10,
      "200": 20
    },
    sizePricing: {},
    colorPricing: {},
    finishingOptions: {},
    allowedPrintPositions: []
  };

  // Get active Shopify configuration or fall back to defaults
  const config = Object.assign({}, DEFAULT_CONFIG, window.ShopifyConfigurator || {});

  // Normalized price maps and lists
  const normalizedSizePricing = {};
  const normalizedColorPricing = {};
  const normalizedDiscountTiers = {};
  const availableColors = [];
  const availableSizes = [];

  function parsePricingMetafields() {
    // Parse size pricing
    const sizePricingRaw = config.sizePricing || {};
    for (const key in sizePricingRaw) {
      const val = sizePricingRaw[key];
      if (Array.isArray(val)) {
        // Format: { "4.99": ["XS", "S", "M"] }
        val.forEach(size => {
          normalizedSizePricing[size.toLowerCase()] = parseFloat(key);
          if (!availableSizes.includes(size)) availableSizes.push(size);
        });
      } else {
        // Format: { "XS": 4.99 }
        normalizedSizePricing[key.toLowerCase()] = parseFloat(val);
        if (!availableSizes.includes(key)) availableSizes.push(key);
      }
    }

    // Parse color pricing
    const colorPricingRaw = config.colorPricing || {};
    for (const key in colorPricingRaw) {
      const val = colorPricingRaw[key];
      if (Array.isArray(val)) {
        // Format: { "0.50": ["Red", "Yellow"] }
        val.forEach(color => {
          normalizedColorPricing[color.toLowerCase()] = parseFloat(key);
          if (!availableColors.includes(color)) availableColors.push(color);
        });
      } else {
        // Format: { "Red": 0.50 }
        normalizedColorPricing[key.toLowerCase()] = parseFloat(val);
        if (!availableColors.includes(key)) availableColors.push(key);
      }
    }

    // Parse discount tiers (extract numbers from ranges like "30-99")
    const discountTiersRaw = config.discountTiers || {};
    for (const key in discountTiersRaw) {
      const match = key.match(/^\d+/);
      if (match) {
        const threshold = parseInt(match[0]);
        normalizedDiscountTiers[threshold] = parseFloat(discountTiersRaw[key]) || 0;
      }
    }
  }

  parsePricingMetafields();

  // 2. Global State Object
  window.configuratorState = {
    productId: config.productId,
    productName: config.productName || "Product",
    colors: [],         // Array of selected colors: ['Black', 'Navy']
    quantities: {},     // Map of color -> { size: qty }: { 'Black': { 'S': 10, 'M': 15 } }
    printPositions: [], // Selected positions: ['Front Chest', 'Back']
    fileUploads: [],    // Uploaded file details: [{ name: 'logo.pdf', url: 'https://...' }]
    notes: "",          // Production instructions
    pricing: {
      totalQuantity: 0,
      appliedDiscountPercent: 0,
      subtotalBeforeDiscount: 0,
      totalPrice: 0,
      isQuoteRequest: false,
      isOrderAllowed: false,
      errorMessage: ""
    }
  };

  /**
   * Standalone Pricing Engine
   * Calculates the subtotal, discounts, and final total based on state and config.
   */
  window.calculatePrice = function() {
    const state = window.configuratorState;
    const quantities = state.quantities;

    let totalQuantity = 0;
    let subtotalBeforeDiscount = 0;

    const baseVariantPrice = parseFloat(config.basePrice) || 0;

    // Loop through colors and sizes to sum quantities and compute subtotal
    for (const color in quantities) {
      const colorSurcharge = normalizedColorPricing[color.toLowerCase()] || 0;

      for (const size in quantities[color]) {
        const qty = parseInt(quantities[color][size]) || 0;
        if (qty <= 0) continue;

        totalQuantity += qty;

        const sizeBasePrice = normalizedSizePricing[size.toLowerCase()] !== undefined 
          ? normalizedSizePricing[size.toLowerCase()] 
          : baseVariantPrice;

        const itemUnitPrice = sizeBasePrice + colorSurcharge;
        subtotalBeforeDiscount += itemUnitPrice * qty;
      }
    }

    // Determine applied discount tier
    let appliedDiscountPercent = 0;
    const sortedThresholds = Object.keys(normalizedDiscountTiers)
      .map(k => parseInt(k))
      .sort((a, b) => a - b);

    for (const threshold of sortedThresholds) {
      if (totalQuantity >= threshold) {
        appliedDiscountPercent = normalizedDiscountTiers[threshold] || 0;
      }
    }

    // Apply validation checks
    const minQty = parseInt(config.minimumQuantity) || 15;
    const quoteThreshold = parseInt(config.quoteThreshold) || 500;
    
    let isQuoteRequest = totalQuantity >= quoteThreshold;
    let isOrderAllowed = totalQuantity >= minQty;
    let errorMessage = "";

    if (totalQuantity > 0 && totalQuantity < minQty) {
      errorMessage = `Mindestbestellmenge von ${minQty} Stück. Ihr habt aktuell ${totalQuantity} Stück gewählt.`;
      isOrderAllowed = false;
    } else if (isQuoteRequest) {
      errorMessage = `Menge übersteigt ${quoteThreshold} Stück. Bitte stellen Sie eine kostenlose Designanfrage über Tab 2.`;
      isOrderAllowed = false;
    } else if (totalQuantity === 0) {
      errorMessage = "Bitte wählen Sie mindestens ein Produkt aus.";
      isOrderAllowed = false;
    }

    // Calculate final total
    const discountAmount = subtotalBeforeDiscount * (appliedDiscountPercent / 100);
    const totalPrice = subtotalBeforeDiscount - discountAmount;

    // Update pricing state
    state.pricing = {
      totalQuantity,
      appliedDiscountPercent,
      subtotalBeforeDiscount,
      totalPrice,
      isQuoteRequest,
      isOrderAllowed,
      errorMessage
    };

    // Dispatch custom DOM event to trigger UI updates
    const event = new CustomEvent('configurator:state-change', { detail: state });
    document.dispatchEvent(event);

    return state.pricing;
  };

  /**
   * Helper function to update the global state and recalculate pricing.
   */
  window.updateConfiguratorState = function(updates) {
    Object.assign(window.configuratorState, updates);
    return window.calculatePrice();
  };

  // --- UI RENDERING ENGINE ---

  // Helper to format currency
  function formatMoney(amount) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  }

  // Parse product options and variants from Shopify configuration
  function getProductOptions() {
    const variants = config.variants || [];
    const options = config.options || [];
    const optionSwatches = config.optionSwatches || {};
    const colorImages = {};

    // 1. Read category swatches passed from Liquid
    for (const c in optionSwatches) {
      if (optionSwatches[c]) {
        let swatchImg = optionSwatches[c].image || null;
        if (swatchImg && swatchImg.startsWith('//')) {
          swatchImg = 'https:' + swatchImg;
        }
        colorImages[c] = {
          color: optionSwatches[c].color || null,
          image: swatchImg
        };
      }
    }

    // 2. Read variant images
    const colorIndex = options.findIndex(o => /color|farbe|colour/i.test(o));
    if (colorIndex !== -1) {
      variants.forEach(variant => {
        const colorVal = variant.options ? variant.options[colorIndex] : variant[`option${colorIndex + 1}`];
        if (colorVal) {
          let imgUrl = null;
          if (typeof variant.featured_image === 'string') {
            imgUrl = variant.featured_image;
          } else if (variant.featured_image && variant.featured_image.src) {
            imgUrl = variant.featured_image.src;
          } else if (variant.image) {
            imgUrl = typeof variant.image === 'string' ? variant.image : variant.image.src;
          }

          if (imgUrl) {
            if (imgUrl.startsWith('//')) {
              imgUrl = 'https:' + imgUrl;
            }
            const existingColor = colorImages[colorVal] ? colorImages[colorVal].color : null;
            const existingImg = colorImages[colorVal] ? colorImages[colorVal].image : null;

            colorImages[colorVal] = {
              color: existingColor,
              image: existingImg || imgUrl
            };
          }
        }
      });
    }

    // If Available Colors or Sizes from Metafields are empty, extract them from Variants as fallback
    let finalColors = [...availableColors];
    let finalSizes = [...availableSizes];

    if (finalColors.length === 0 && colorIndex !== -1) {
      const colorsSet = new Set();
      variants.forEach(variant => {
        const colorVal = variant[`option${colorIndex + 1}`];
        if (colorVal) colorsSet.add(colorVal);
      });
      finalColors = Array.from(colorsSet);
    }

    const sizeIndex = options.findIndex(o => /size|größe|groesse/i.test(o));
    if (finalSizes.length === 0 && sizeIndex !== -1) {
      const sizesSet = new Set();
      variants.forEach(variant => {
        const sizeVal = variant[`option${sizeIndex + 1}`];
        if (sizeVal) sizesSet.add(sizeVal);
      });
      finalSizes = Array.from(sizesSet);
    }

    // Custom sorting of sizes (standard size sequence)
    const sizeOrder = ['3xs', '2xs', 'xs', 's', 'm', 'l', 'xl', 'xxl', '3xl', '4xl', '5xl'];
    const sortedSizes = finalSizes.sort((a, b) => {
      const idxA = sizeOrder.indexOf(a.toLowerCase());
      const idxB = sizeOrder.indexOf(b.toLowerCase());
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.localeCompare(b);
    });

    return {
      colors: finalColors.length > 0 ? finalColors : ["Standard"],
      sizes: sortedSizes.length > 0 ? sortedSizes : ["One Size"],
      colorImages
    };
  }

  window.renderConfigurator = function() {
    const container = document.getElementById('configurator-app');
    if (!container) return;

    const { colors, sizes, colorImages } = getProductOptions();
    const state = window.configuratorState;

    let html = '';

    // Step 6: Color Selector HTML
    html += `
      <div class="color-selector-wrapper mb-8" style="margin-top: 1rem; margin-bottom: 2rem;">
        <div class="flex items-center justify-between mb-3">
          <label class="block text-sm font-extrabold text-foreground uppercase tracking-wider">1. Farben auswählen</label>
          <span class="text-xs font-semibold text-muted-foreground">${state.colors.length > 0 ? `${state.colors.length} ausgewählt` : 'Eine oder mehrere wählen'}</span>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
          ${colors.map(color => {
            const isSelected = state.colors.includes(color);
            const swatchInfo = colorImages[color] || {};
            const imgSrc = swatchInfo.image || '';
            const hexColor = swatchInfo.color || '';
            const surcharge = normalizedColorPricing[color.toLowerCase()] || 0;
            const surchargeLabel = surcharge > 0 ? `+${formatMoney(surcharge)}` : '';

            let thumbnailHtml = `
              <div class="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center text-xs font-black text-foreground/40 border border-border/40 flex-shrink-0 shadow-sm">
                ${color.substring(0, 2).toUpperCase()}
              </div>
            `;
            if (imgSrc) {
              thumbnailHtml = `
                <div class="w-11 h-11 rounded-xl overflow-hidden border border-black/10 flex-shrink-0 shadow-sm bg-gray-50 flex items-center justify-center">
                  <img src="${imgSrc}" class="w-full h-full object-cover" alt="${color}" loading="lazy" />
                </div>
              `;
            } else if (hexColor) {
              thumbnailHtml = `
                <div class="w-11 h-11 rounded-xl border border-black/15 shadow-inner flex-shrink-0" style="background-color: ${hexColor}"></div>
              `;
            }

            return `
              <div 
                role="button"
                tabindex="0"
                class="color-card-item relative group flex items-center justify-between p-3 rounded-2xl border-2 transition-all duration-200 cursor-pointer select-none ${isSelected ? 'border-primary bg-primary/[0.04] shadow-md shadow-primary/5 ring-2 ring-primary/20' : 'border-border/70 bg-white hover:border-primary/40 hover:shadow-sm'}"
                data-color="${color}"
                data-img="${imgSrc}"
              >
                <div class="flex items-center gap-3 min-w-0">
                  ${thumbnailHtml}
                  <div class="flex flex-col min-w-0">
                    <span class="text-sm font-bold text-foreground leading-snug truncate ${isSelected ? 'text-primary' : ''}">${color}</span>
                    <span class="text-xs font-semibold ${surcharge > 0 ? 'text-primary' : 'text-muted-foreground/80'}">${surchargeLabel || 'Standard'}</span>
                  </div>
                </div>

                ${imgSrc ? `
                  <span 
                    role="button"
                    tabindex="0"
                    title="Vorschau vergrößern"
                    class="zoom-color-trigger w-7 h-7 rounded-full bg-gray-100/90 hover:bg-primary hover:text-white flex items-center justify-center text-xs text-muted-foreground transition-all flex-shrink-0 ml-1 shadow-sm"
                    data-color="${color}"
                    data-img="${imgSrc}"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/><path d="M11 8v6"/><path d="M8 11h6"/></svg>
                  </span>
                ` : ''}

                ${isSelected ? `
                  <div class="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-black shadow-sm">
                    ✓
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;

    // Step 7: Size Matrix HTML
    if (state.colors.length > 0) {
      html += `
        <div class="size-matrix-wrapper mb-6">
          <label class="block text-sm font-bold text-foreground mb-3 uppercase tracking-wider">2. Größen & Mengen eintragen</label>
          <div class="flex flex-col gap-3.5" style="display: flex !important; flex-direction: column !important; gap: 0.875rem !important;">
            ${state.colors.map(color => {
              const colorSurcharge = normalizedColorPricing[color.toLowerCase()] || 0;

              return `
                <div class="color-quantity-box bg-gray-50 rounded-2xl p-4 sm:p-5 border border-border/50 shadow-sm">
                  <h4 class="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                    <span class="w-3 h-3 rounded-full bg-primary flex-shrink-0"></span>
                    Mengen für: ${color}
                  </h4>
                  <div class="grid grid-cols-3 gap-2 font-semibold text-xs text-muted-foreground pb-2 border-b border-border/40 mb-3">
                    <span>Größe</span>
                    <span class="text-center">Stückpreis</span>
                    <span class="text-right">Stückzahl</span>
                  </div>
                  <div class="space-y-3">
                    ${sizes.map(size => {
                      const basePrice = parseFloat(config.basePrice) || 0;
                      const sizeBasePrice = normalizedSizePricing[size.toLowerCase()] !== undefined 
                        ? normalizedSizePricing[size.toLowerCase()] 
                        : basePrice;

                      const unitPrice = sizeBasePrice + colorSurcharge;
                      const currentQty = (state.quantities[color] && state.quantities[color][size]) || 0;

                      return `
                        <div class="grid grid-cols-3 gap-2 items-center">
                          <span class="text-sm font-bold text-foreground">${size}</span>
                          <span class="text-sm text-center text-foreground/80">${formatMoney(unitPrice)}</span>
                          <div class="flex items-center justify-end gap-1">
                            <input 
                              type="number" 
                              min="0" 
                              value="${currentQty}" 
                              data-color="${color}" 
                              data-size="${size}"
                              class="size-qty-input w-20 text-center border-2 border-border/80 rounded-xl py-1.5 focus:outline-none focus:border-primary text-sm font-bold text-foreground"
                            />
                          </div>
                        </div>
                      `;
                    }).join('')}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    } else {
      html += `
        <div class="p-6 text-center bg-gray-50 rounded-2xl border border-border/40 text-muted-foreground text-sm mb-6">
          Bitte wählen Sie zuerst mindestens eine Farbe aus.
        </div>
      `;
    }

    // Step 10: Print Positions Component HTML
    const printPositions = config.allowedPrintPositions.length > 0 ? config.allowedPrintPositions : ['Brust links', 'Brust mittig', 'Rücken mittig', 'Ärmel links', 'Ärmel rechts'];
    html += `
      <div class="print-positions-wrapper mb-6">
        <label class="block text-sm font-bold text-foreground mb-3 uppercase tracking-wider">3. Druckpositionen</label>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
          ${printPositions.map(pos => {
            const isChecked = state.printPositions.includes(pos);
            return `
              <label class="flex items-center gap-3 p-3 rounded-xl border border-border/60 hover:border-primary/40 bg-white cursor-pointer select-none">
                <input type="checkbox" 
                  class="print-position-checkbox rounded border-gray-300 text-primary focus:ring-primary h-5 w-5 cursor-pointer"
                  data-position="${pos}"
                  ${isChecked ? 'checked' : ''}
                />
                <span class="text-sm font-semibold text-foreground/80">${pos}</span>
              </label>
            `;
          }).join('')}
        </div>
      </div>
    `;

    // Step 11: File Upload Component HTML
    html += `
      <div class="file-upload-wrapper mb-6">
        <label class="block text-sm font-bold text-foreground mb-2 uppercase tracking-wider">4. Logo / Design hochladen</label>
        <div class="flex items-center justify-center w-full">
          <label class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border/80 hover:border-primary/60 rounded-2xl cursor-pointer bg-white transition-all">
            <div class="flex flex-col items-center justify-center pt-5 pb-6">
              <svg class="w-8 h-8 mb-2 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
              <p class="text-xs font-semibold text-muted-foreground">PDF, PNG, SVG oder AI Datei auswählen</p>
            </div>
            <input type="file" id="configurator-file-upload" class="hidden" accept=".pdf,.png,.svg,.ai" />
          </label>
        </div>
        ${state.fileUploads.length > 0 ? `
          <div class="mt-3 space-y-2">
            ${state.fileUploads.map(file => `
              <div class="flex items-center justify-between p-2 bg-primary/5 rounded-xl border border-primary/20 text-xs font-semibold text-primary">
                <span>📄 ${file.name}</span>
                <button type="button" class="remove-file-btn text-red-500 hover:text-red-700 font-bold px-2" data-name="${file.name}">Entfernen</button>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `;

    // Step 12: Notes Component HTML
    html += `
      <div class="notes-wrapper mb-6">
        <label class="block text-sm font-bold text-foreground mb-2 uppercase tracking-wider" for="configurator-notes">5. Besondere Wünsche / Notizen</label>
        <textarea 
          id="configurator-notes"
          rows="3"
          placeholder="z.B. Wunschfarben, Details zum Aufdruck, etc."
          class="w-full border-2 border-border/80 rounded-xl p-3 focus:outline-none focus:border-primary text-sm font-medium text-foreground"
        >${state.notes}</textarea>
      </div>
    `;

    // Step 8 & 9: Live Price Panel HTML
    const pricing = state.pricing;
    
    html += `
      <div class="price-panel-wrapper bg-gray-50 border border-border/40 rounded-2xl p-5 mb-6">
        <h4 class="text-sm font-bold text-foreground mb-4 uppercase tracking-wider">6. Zusammenfassung & Preis</h4>
        <div class="space-y-2 text-sm font-semibold text-foreground/80">
          <div class="flex justify-between">
            <span>Gesamtzahl Textilien:</span>
            <span id="summary-total-qty" class="font-bold text-foreground">${pricing.totalQuantity} Stück</span>
          </div>
          
          <div id="summary-discount-row" class="flex justify-between text-green-600 ${pricing.appliedDiscountPercent > 0 ? '' : 'hidden'}">
            <span>Mengenrabatt (<span id="summary-discount-percent">${pricing.appliedDiscountPercent}</span>%):</span>
            <span id="summary-discount-amount">-${formatMoney(pricing.subtotalBeforeDiscount * (pricing.appliedDiscountPercent / 100))}</span>
          </div>

          <div class="flex justify-between border-t border-border/40 pt-3 text-base">
            <span class="font-bold text-foreground">Gesamtpreis:</span>
            <span id="summary-total-price" class="font-bold text-primary text-lg">${pricing.isQuoteRequest ? 'Individuelles Angebot' : formatMoney(pricing.totalPrice)}</span>
          </div>
        </div>

        <div id="summary-error-msg" class="mt-4 p-3 rounded-xl text-xs font-semibold ${pricing.errorMessage ? 'block' : 'hidden'} ${pricing.isQuoteRequest ? 'bg-blue-50 border border-blue-200 text-blue-700' : 'bg-red-50 border border-red-200 text-red-700'}">
          ${pricing.isQuoteRequest ? 'ℹ️' : '⚠️'} <span id="summary-error-text">${pricing.errorMessage || ''}</span>
        </div>
      </div>
    `;

    // Step 14: Add to Cart Button HTML
    html += `
      <button 
        type="button" 
        id="configurator-submit-btn"
        class="w-full h-14 font-bold text-lg rounded-full shadow-md hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-2 ${pricing.isOrderAllowed ? 'bg-primary text-primary-foreground' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}"
        ${pricing.isOrderAllowed ? '' : 'disabled'}
      >
        📄 In den Warenkorb legen
      </button>
    `;

    container.innerHTML = html;
    bindConfiguratorEvents();
  };

  // Update price panel DOM elements directly
  function updatePricePanel(state) {
    const pricing = state.pricing;

    const totalQtyEl = document.getElementById('summary-total-qty');
    const discountRowEl = document.getElementById('summary-discount-row');
    const discountPercentEl = document.getElementById('summary-discount-percent');
    const discountAmountEl = document.getElementById('summary-discount-amount');
    const totalPriceEl = document.getElementById('summary-total-price');
    const errorMsgEl = document.getElementById('summary-error-msg');
    const errorTextEl = document.getElementById('summary-error-text');
    const submitBtn = document.getElementById('configurator-submit-btn');

    if (totalQtyEl) totalQtyEl.textContent = `${pricing.totalQuantity} Stück`;

    if (discountRowEl) {
      if (pricing.appliedDiscountPercent > 0) {
        discountRowEl.classList.remove('hidden');
        if (discountPercentEl) discountPercentEl.textContent = pricing.appliedDiscountPercent;
        if (discountAmountEl) {
          const discountAmt = pricing.subtotalBeforeDiscount * (pricing.appliedDiscountPercent / 100);
          discountAmountEl.textContent = `-${formatMoney(discountAmt)}`;
        }
      } else {
        discountRowEl.classList.add('hidden');
      }
    }

    if (totalPriceEl) {
      totalPriceEl.textContent = pricing.isQuoteRequest ? 'Individuelles Angebot' : formatMoney(pricing.totalPrice);
    }

    if (errorMsgEl && errorTextEl) {
      if (pricing.errorMessage) {
        errorMsgEl.classList.remove('hidden');
        errorTextEl.textContent = pricing.errorMessage;
        
        // Adjust colors
        if (pricing.isQuoteRequest) {
          errorMsgEl.className = "mt-4 p-3 rounded-xl text-xs font-semibold bg-blue-50 border border-blue-200 text-blue-700";
        } else {
          errorMsgEl.className = "mt-4 p-3 rounded-xl text-xs font-semibold bg-red-50 border border-red-200 text-red-700";
        }
      } else {
        errorMsgEl.classList.add('hidden');
      }
    }

    if (submitBtn) {
      submitBtn.disabled = !pricing.isOrderAllowed;
      if (pricing.isOrderAllowed) {
        submitBtn.className = "w-full h-14 font-bold text-lg rounded-full shadow-md hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-2 bg-primary text-primary-foreground";
      } else {
        submitBtn.className = "w-full h-14 font-bold text-lg rounded-full shadow-md hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-2 bg-gray-300 text-gray-500 cursor-not-allowed";
      }
    }
  }

  function openColorLightboxModal(colorName, imgSrc) {
    if (!imgSrc) return;
    let modal = document.getElementById('color-lightbox-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'color-lightbox-modal';
      modal.className = 'fixed inset-0 z-[99999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 transition-opacity duration-200 opacity-0 pointer-events-none';
      modal.innerHTML = `
        <div class="relative bg-white rounded-[2rem] p-6 sm:p-8 max-w-xl w-full shadow-2xl overflow-hidden flex flex-col items-center gap-5 transform scale-95 transition-transform duration-200" id="lightbox-modal-content">
          <button type="button" id="close-lightbox-btn" class="absolute top-5 right-5 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-foreground font-bold text-lg transition-all shadow-sm">✕</button>
          
          <div class="flex items-center gap-2.5 w-full pr-12">
            <span class="w-3.5 h-3.5 rounded-full bg-primary flex-shrink-0"></span>
            <div class="flex flex-col">
              <span class="text-xs font-bold text-muted-foreground uppercase tracking-widest">Farbvorschau</span>
              <h3 id="lightbox-color-title" class="text-xl font-extrabold text-foreground tracking-tight"></h3>
            </div>
          </div>
          
          <div class="w-full aspect-square max-h-[60vh] rounded-2xl overflow-hidden bg-gray-50 flex items-center justify-center border border-border/40 shadow-inner p-4">
            <img id="lightbox-color-img" src="" class="object-contain w-full h-full rounded-xl transition-all" alt="Color Preview" />
          </div>

          <button type="button" id="lightbox-select-btn" class="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-wide shadow-md transition-all flex items-center justify-center gap-2">
            Diese Farbe auswählen
          </button>
        </div>
      `;
      document.body.appendChild(modal);

      const closeModal = () => {
        modal.classList.add('opacity-0', 'pointer-events-none');
        modal.querySelector('#lightbox-modal-content').classList.add('scale-95');
      };

      modal.querySelector('#close-lightbox-btn').addEventListener('click', closeModal);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeModal();
      });
    }

    modal.querySelector('#lightbox-color-title').textContent = colorName;
    modal.querySelector('#lightbox-color-img').src = imgSrc;
    
    // Select button inside modal
    const selectBtn = modal.querySelector('#lightbox-select-btn');
    selectBtn.onclick = () => {
      const state = window.configuratorState;
      if (!state.colors.includes(colorName)) {
        state.colors.push(colorName);
        state.quantities[colorName] = {};
        window.updateConfiguratorState({ colors: state.colors });
        window.renderConfigurator();
      }
      modal.classList.add('opacity-0', 'pointer-events-none');
      modal.querySelector('#lightbox-modal-content').classList.add('scale-95');
    };

    modal.classList.remove('opacity-0', 'pointer-events-none');
    modal.querySelector('#lightbox-modal-content').classList.remove('scale-95');
  }

  // Bind active events to the rendered HTML elements
  function bindConfiguratorEvents() {
    // Color item click handlers
    document.querySelectorAll('.color-card-item').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.zoom-color-trigger')) {
          e.stopPropagation();
          const zoomBtn = e.target.closest('.zoom-color-trigger');
          const color = zoomBtn.getAttribute('data-color');
          const imgSrc = zoomBtn.getAttribute('data-img');
          openColorLightboxModal(color, imgSrc);
          return;
        }

        const color = card.getAttribute('data-color');
        const imgSrc = card.getAttribute('data-img');
        const state = window.configuratorState;
        
        // Sync main product image on left column
        if (imgSrc) {
          const mainImg = document.querySelector('.main-image-container img');
          if (mainImg) mainImg.src = imgSrc;
        }

        let newColors = [...state.colors];
        if (newColors.includes(color)) {
          newColors = newColors.filter(c => c !== color);
          if (state.quantities[color]) delete state.quantities[color];
        } else {
          newColors.push(color);
          state.quantities[color] = {};
        }

        window.updateConfiguratorState({ colors: newColors });
        window.renderConfigurator();
      });

      // Keyboard accessibility
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          card.click();
        }
      });
    });

    // Quantity inputs handler - listening to both input and change for instant real-time response
    document.querySelectorAll('.size-qty-input').forEach(input => {
      const handleInput = () => {
        const color = input.getAttribute('data-color');
        const size = input.getAttribute('data-size');
        const qty = parseInt(input.value) || 0;

        const state = window.configuratorState;
        if (!state.quantities[color]) state.quantities[color] = {};
        state.quantities[color][size] = qty;

        window.calculatePrice();
      };
      
      input.addEventListener('input', handleInput);
      input.addEventListener('change', handleInput);
    });

    // Print positions handler
    document.querySelectorAll('.print-position-checkbox').forEach(box => {
      box.addEventListener('change', () => {
        const pos = box.getAttribute('data-position');
        const state = window.configuratorState;
        
        let newPos = [...state.printPositions];
        if (box.checked) {
          if (!newPos.includes(pos)) newPos.push(pos);
        } else {
          newPos = newPos.filter(p => p !== pos);
        }

        window.updateConfiguratorState({ printPositions: newPos });
      });
    });

    // File Upload Handler (with Mock Service uploading to file.io)
    const fileUpload = document.getElementById('configurator-file-upload');
    if (fileUpload) {
      fileUpload.addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Show uploading feedback
        const originalLabel = fileUpload.parentElement.innerHTML;
        fileUpload.parentElement.innerHTML = `
          <div class="flex flex-col items-center justify-center pt-5 pb-6">
            <svg class="animate-spin w-8 h-8 mb-2 text-primary" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
            <p class="text-xs font-semibold text-primary">Datei wird hochgeladen...</p>
          </div>
        `;

        try {
          // Perform real file upload to file.io (anonymous public host for files)
          const formData = new FormData();
          formData.append('file', file);

          const res = await fetch('https://file.io', {
            method: 'POST',
            body: formData
          });

          if (!res.ok) throw new Error('Upload failed');
          const json = await res.json();

          const fileUrl = json.link; // Real working file link
          const state = window.configuratorState;
          
          window.updateConfiguratorState({
            fileUploads: [...state.fileUploads, { name: file.name, url: fileUrl }]
          });
        } catch (error) {
          console.error("Upload error:", error);
          // Fallback to mock URL so user is not blocked on local development/testing
          const mockUrl = `https://cdn.shopify.com/s/files/mock-upload-${Date.now()}-${file.name}`;
          const state = window.configuratorState;
          
          window.updateConfiguratorState({
            fileUploads: [...state.fileUploads, { name: file.name + ' (Mock)', url: mockUrl }]
          });
          
          alert("Hinweis: Datei-Upload fehlgeschlagen (Netzwerk/CORS). Es wurde eine temporäre Mock-URL generiert, damit Sie die Bestellung weiterhin testen können.");
        }

        window.renderConfigurator();
      });
    }

    // Remove file handler
    document.querySelectorAll('.remove-file-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const name = btn.getAttribute('data-name');
        const state = window.configuratorState;
        
        const newFiles = state.fileUploads.filter(f => f.name !== name);
        window.updateConfiguratorState({ fileUploads: newFiles });
        window.renderConfigurator();
      });
    });

    // Notes handler
    const notesArea = document.getElementById('configurator-notes');
    if (notesArea) {
      notesArea.addEventListener('input', () => {
        window.configuratorState.notes = notesArea.value;
      });
    }

    // Step 14: Submit to Cart handler
    const submitBtn = document.getElementById('configurator-submit-btn');
    if (submitBtn) {
      submitBtn.addEventListener('click', async () => {
        const state = window.configuratorState;
        if (!state.pricing.isOrderAllowed) return;

        submitBtn.innerHTML = `
          <svg class="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
          In den Warenkorb...
        `;
        submitBtn.disabled = true;

        try {
          const itemsToAdd = [];

          // Group variants by color
          const variants = config.variants || [];

          // Read active non-configurator options (like Age Group, Target Gender, Neckline, Sleeve Type, Top Length)
          const activeOptions = {};
          document.querySelectorAll('.product-option-select').forEach(select => {
            const name = select.getAttribute('name').replace('options[', '').replace(']', '');
            activeOptions[name.toLowerCase()] = select.value.toLowerCase();
          });

          for (const color in state.quantities) {
            const sizeQty = state.quantities[color];
            let totalQtyForColor = 0;
            const sizesObj = {};

            for (const size in sizeQty) {
              const qty = sizeQty[size];
              if (qty > 0) {
                totalQtyForColor += qty;
                sizesObj[size] = qty;
              }
            }

            if (totalQtyForColor <= 0) continue;

            // Match variant matching the active style options (Gender, Neckline) AND the current color
            const options = config.options || [];
            const colorIndex = options.findIndex(o => /color|farbe|colour/i.test(o));
            
            let matchedVariant = variants.find(v => {
              // Ensure variant matches selected dropdowns (Schnitt/Gender, Age group, etc.)
              let dropdownsMatch = true;
              options.forEach((optName, idx) => {
                const optNameLower = optName.toLowerCase();
                // Skip color and size options
                if (/color|farbe|colour|size|größe|groesse/i.test(optNameLower)) return;
                
                const val = v[`option${idx + 1}`];
                const activeVal = activeOptions[optNameLower];
                if (val && activeVal && val.toLowerCase() !== activeVal) {
                  dropdownsMatch = false;
                }
              });

              // Ensure variant matches this color
              let colorMatches = true;
              if (colorIndex !== -1) {
                const val = v[`option${colorIndex + 1}`];
                if (val && val.toLowerCase() !== color.toLowerCase()) {
                  colorMatches = false;
                }
              }

              return dropdownsMatch && colorMatches;
            });

            // Fallback: If no strict variant matches (due to variant option permutations), use the first matching color variant
            if (!matchedVariant && colorIndex !== -1) {
              matchedVariant = variants.find(v => {
                const val = v[`option${colorIndex + 1}`];
                return val && val.toLowerCase() === color.toLowerCase();
              });
            }

            // Ultimate fallback to first variant
            const variantId = matchedVariant ? matchedVariant.id : (variants.length > 0 ? variants[0].id : null);

            // Calculate custom price for this color group
            // Uses normalizedSizePricing/normalizedColorPricing (parsed at init, available via closure)
            // to match the same logic as calculatePrice()
            let itemSubtotal = 0;
            const sizeList = [];
            const colorSurcharge = normalizedColorPricing[color.toLowerCase()] || 0;

            for (const size in sizesObj) {
              const qty = sizesObj[size];
              const sizeBasePrice = normalizedSizePricing[size.toLowerCase()] !== undefined 
                ? normalizedSizePricing[size.toLowerCase()] 
                : (parseFloat(config.basePrice) || 0);
              
              const unitPrice = sizeBasePrice + colorSurcharge;
              itemSubtotal += unitPrice * qty;
              sizeList.push(`${size} (${qty}x)`);
            }

            const discountMultiplier = (1 - state.pricing.appliedDiscountPercent / 100);
            const itemFinalPrice = itemSubtotal * discountMultiplier;

            // Step 13: Build Cart Payload
            const props = {
              "Farbe": color,
              "Größen": sizeList.join(', '),
              "_product": state.productName,
              "_color": color,
              "_sizes": JSON.stringify(sizesObj),
              "_discount_tier": `${state.pricing.appliedDiscountPercent}%`,
              "_discount_percent": state.pricing.appliedDiscountPercent.toString(),
              "_subtotal_before_discount": itemSubtotal.toFixed(2), // Pre-discount line subtotal
              "_line_price": itemFinalPrice.toFixed(2), // Calculated line total price after discount
              "_unit_price": (itemFinalPrice / totalQtyForColor).toFixed(2)
            };

            if (state.printPositions.length > 0) {
              props["Druckpositionen"] = state.printPositions.join(', ');
            }
            if (state.fileUploads.length > 0) {
              props["Artwork"] = state.fileUploads.map(f => f.url).join(', ');
            }
            if (state.notes.trim() !== "") {
              props["Notizen"] = state.notes;
            }

            itemsToAdd.push({
              id: variantId,
              quantity: totalQtyForColor,
              properties: props
            });
          }

          // Add items in bulk to Shopify Cart
          const cartResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: itemsToAdd })
          });

          if (!cartResponse.ok) throw new Error('Cart API failure');
          
          // Redirect to Cart Page to verify
          window.location.href = '/cart';
        } catch (error) {
          console.error("Cart submission error:", error);
          alert("Fehler beim Hinzufügen zum Warenkorb. Bitte versuchen Sie es erneut.");
          submitBtn.innerHTML = `📄 In den Warenkorb legen`;
          submitBtn.disabled = false;
        }
      });
    }
  }

  /**
   * Pre-fill configurator from URL parameters (for cart "Ändern" edit flow).
   * Reads: edit=true, edit_color, edit_sizes, edit_prints, edit_notes
   */
  function initFromUrlParams() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('edit') !== 'true') return;

    const state = window.configuratorState;

    // Pre-select the color from this line item
    const editColor = params.get('edit_color');
    if (editColor) {
      state.colors = [editColor];
      state.quantities[editColor] = {};
    }

    // Pre-fill size quantities
    const editSizes = params.get('edit_sizes');
    if (editSizes && editColor) {
      try {
        const sizesObj = JSON.parse(decodeURIComponent(editSizes));
        state.quantities[editColor] = {};
        for (const size in sizesObj) {
          state.quantities[editColor][size] = parseInt(sizesObj[size]) || 0;
        }
      } catch (e) {
        console.warn('Could not parse edit_sizes:', e);
      }
    }

    // Pre-fill print positions
    const editPrints = params.get('edit_prints');
    if (editPrints) {
      state.printPositions = editPrints.split(',').map(p => p.trim()).filter(Boolean);
    }

    // Pre-fill notes
    const editNotes = params.get('edit_notes');
    if (editNotes) {
      state.notes = decodeURIComponent(editNotes);
    }

    // Calculate pricing with pre-filled state
    window.calculatePrice();
  }

  // Handle Event listening to update DOM pricing panel if rendered outside the main app
  document.addEventListener('configurator:state-change', (e) => {
    updatePricePanel(e.detail);
    // Also update top product price if appropriate
    const pricing = e.detail.pricing;
    const priceDisplay = document.querySelector('.price-container .text-2xl');
    if (priceDisplay && pricing.totalQuantity > 0) {
      priceDisplay.innerHTML = pricing.isQuoteRequest ? 'Individuelles Angebot' : formatMoney(pricing.totalPrice);
    }
  });

  // Render on load
  function initConfigurator() {
    try {
      initFromUrlParams(); // Pre-fill state from URL if editing from cart
      window.renderConfigurator();
    } catch (err) {
      console.error('Configurator init error:', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initConfigurator);
  } else {
    initConfigurator();
  }
})();
